import { Fragment, useLayoutEffect, useRef, useState, type ChangeEvent, type KeyboardEvent } from 'react';
import useOnclickOutside from 'react-cool-onclickoutside';
import { useDropdownSide } from '@/app/common/hooks/useDropdownSide.ts';
import type { FilterDataOption } from '@/app/common/filters/core/filtersTypes.ts';
import LabelNode from '@/app/common/filters/types/shared/LabelNode.tsx';
import TsxCheckbox from '@/app/common/filters/types/shared/TsxCheckbox.tsx';
import { toggleOnEnter } from '@/app/common/filters/types/shared/toggleOnEnter.ts';
import { scrollTopRevealing } from '@/app/common/utils/revealScrollTop.ts';
import { FilterBadgeContent } from '@/app/common/filters/surfaces/badges/FilterBadgeContent.tsx';
import { useFiltersUiData } from '@/app/common/filters/surfaces/provider/filtersUiProvider.tsx';
import {
  Container,
  InputWrapper,
  SearchIcon,
  StyledInput,
  InputLabel,
  DropdownOverlay,
  OptionItem,
  OptionDivider,
  SelectedChips,
  NoResults,
  SelectedOptionItem,
} from '@/app/common/filters/types/shared/styled/FilterSearchList.styled.ts';

interface FilterSearchListProps {
  name: string;
  placeholder: string;
  noResultsText: string;
  options: FilterDataOption[];
  anyValue?: string;
  disabled?: boolean;
  onToggle: (value: string) => void;
}

const FilterSearchList = ({
  name,
  placeholder,
  noResultsText,
  options,
  anyValue,
  disabled,
  onToggle,
}: FilterSearchListProps) => {
  const { useEvoFilters } = useFiltersUiData();
  const containerRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const chipsRef = useRef<HTMLDivElement>(null);
  const selectedListRef = useRef<HTMLUListElement>(null);
  const removedIndex = useRef<number | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isNavigating, setIsNavigating] = useState(false);
  const selectedOptions = options.filter((o) => o.selected);
  const unselectedOptions = options.filter((o) => !o.selected);
  const isOptionDisabled = (option: FilterDataOption) => {
    const staysRemovable = !!useEvoFilters && !!option.selected;
    return !staysRemovable && (!!option.disabled || option.counter === 0);
  };

  const matchingOptions = inputValue
    ? unselectedOptions.filter((opt) =>
        opt.label
          .toString()
          .split(' ')
          .some((word) => word.toLowerCase().startsWith(inputValue.toLowerCase()))
      )
    : unselectedOptions;
  const anyOption = useEvoFilters ? matchingOptions.find((option) => option.value === anyValue) : undefined;
  const filteredOptions = anyOption
    ? [anyOption, ...matchingOptions.filter((option) => option !== anyOption)]
    : matchingOptions;

  const noResults = inputValue.length > 0 && filteredOptions.length === 0;
  const activeOption = isOpen && (isNavigating || !useEvoFilters) ? filteredOptions[currentIndex] : undefined;
  const side = useDropdownSide(fieldRef, overlayRef, isOpen && !!useEvoFilters, filteredOptions.length);

  const openDropdown = () => {
    if (disabled) return;
    setCurrentIndex(0);
    setIsNavigating(false);
    setIsOpen(true);
  };

  const closeDropdown = () => {
    setIsOpen(false);
    setInputValue('');
    setCurrentIndex(0);
    setIsNavigating(false);
  };

  useOnclickOutside(
    () => {
      if (isOpen) closeDropdown();
    },
    {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      refs: [overlayRef as any, containerRef as any],
    }
  );

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    openDropdown();
  };

  const handleSelect = (option: FilterDataOption) => {
    if (disabled || isOptionDisabled(option)) return;
    onToggle(option.value);
    closeDropdown();
  };

  const removeSelected = (option: FilterDataOption, index: number) => {
    if (disabled || isOptionDisabled(option)) return;
    removedIndex.current = index;
    onToggle(option.value);
  };

  useLayoutEffect(() => {
    const index = removedIndex.current;
    removedIndex.current = null;
    if (index === null || document.activeElement !== document.body) return;
    const remaining =
      (chipsRef.current ?? selectedListRef.current)?.querySelectorAll<HTMLElement>('button:not(:disabled)') ?? [];
    const inItsPlace = remaining[Math.min(index, remaining.length - 1)];
    if (inItsPlace) {
      inItsPlace.focus();
    } else {
      inputRef.current?.focus();
      setIsOpen(false);
    }
  }, [selectedOptions.length]);

  const scrollOnFocus = (index: number) => {
    const overlay = overlayRef.current;
    const item = overlay?.querySelector<HTMLLIElement>(`[data-index="${index}"]`);
    if (overlay && item) overlay.scrollTop = scrollTopRevealing(overlay, item);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape' || e.key === 'Tab') {
      closeDropdown();
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      if (activeOption) {
        handleSelect(activeOption);
      } else if (!isOpen) {
        openDropdown();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) {
        openDropdown();
        setIsNavigating(true);
        return;
      }
      setIsNavigating(true);
      if (useEvoFilters && !isNavigating) {
        scrollOnFocus(currentIndex);
        return;
      }
      const next = currentIndex === filteredOptions.length - 1 ? 0 : currentIndex + 1;
      setCurrentIndex(next);
      scrollOnFocus(next);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!isOpen) return;
      setIsNavigating(true);
      const prev = currentIndex === 0 ? filteredOptions.length - 1 : currentIndex - 1;
      setCurrentIndex(prev);
      scrollOnFocus(prev);
    }
  };

  return (
    <Container ref={containerRef} disabled={disabled}>
      <InputWrapper ref={fieldRef} $evo={useEvoFilters} onClick={openDropdown}>
        <SearchIcon
          $evo={useEvoFilters}
          category="UI Essentials"
          name="icon-magnifying-glass"
          size={useEvoFilters ? 20 : 19}
          color={useEvoFilters ? 'iconSecondary' : 'iconPrimary'}
        />
        <InputLabel $evo={useEvoFilters} hasValue={!!inputValue} isFocused={isOpen} htmlFor={`filter-search-${name}`}>
          {placeholder}
        </InputLabel>
        <StyledInput
          $evo={useEvoFilters}
          ref={inputRef}
          id={`filter-search-${name}`}
          name={name}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={openDropdown}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          autoComplete="off"
          role="combobox"
          aria-expanded={isOpen}
          aria-autocomplete="list"
          aria-controls={isOpen ? `filter-search-listbox-${name}` : undefined}
          aria-activedescendant={activeOption ? `filter-search-option-${name}-${currentIndex}` : undefined}
        />
      </InputWrapper>
      {selectedOptions.length > 0 &&
        (useEvoFilters ? (
          <SelectedChips ref={chipsRef}>
            {selectedOptions.map((option, index) => (
              <FilterBadgeContent
                key={option.value}
                label={option.label}
                handleFilterClicked={() => removeSelected(option, index)}
                isLoading={disabled}
              />
            ))}
          </SelectedChips>
        ) : (
          <ul ref={selectedListRef} style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {selectedOptions.map((option, index) => {
              const isDisabled = !!disabled || isOptionDisabled(option);
              return (
                <SelectedOptionItem
                  key={option.value}
                  onKeyDown={toggleOnEnter(isDisabled, () => removeSelected(option, index))}
                >
                  <TsxCheckbox
                    checkbox={{
                      id: `${name}_selected_${index}`,
                      label: <LabelNode option={option} isDisabled={isDisabled} />,
                    }}
                    className="checkbox"
                    onChange={() => removeSelected(option, index)}
                    labelPosition="end"
                    isChecked={true}
                    disabled={isDisabled}
                    fullWidth={true}
                  />
                </SelectedOptionItem>
              );
            })}
          </ul>
        ))}
      {isOpen && (
        <DropdownOverlay
          $evo={useEvoFilters}
          ref={overlayRef}
          data-side={side}
          id={`filter-search-listbox-${name}`}
          role="listbox"
          aria-label={placeholder}
          className="filter-search-dropdown"
          onMouseDown={(e) => e.stopPropagation()}
        >
          {noResults && (
            <NoResults role="status" aria-live="polite">
              {noResultsText}
            </NoResults>
          )}
          {!noResults &&
            filteredOptions.map((option, index) => (
              <Fragment key={option.value}>
                <OptionItem
                  $evo={useEvoFilters}
                  id={`filter-search-option-${name}-${index}`}
                  data-index={index}
                  role="option"
                  aria-selected={option === activeOption}
                  aria-disabled={isOptionDisabled(option) || undefined}
                  isSelected={false}
                  isHighlighted={option === activeOption}
                  isDisabled={isOptionDisabled(option)}
                  tabIndex={-1}
                  onClick={() => handleSelect(option)}
                >
                  <LabelNode option={option} isDisabled={isOptionDisabled(option)} />
                </OptionItem>
                {option === anyOption && filteredOptions.length > 1 && <OptionDivider aria-hidden="true" />}
              </Fragment>
            ))}
        </DropdownOverlay>
      )}
    </Container>
  );
};

export default FilterSearchList;
